import React, {
  MutableRefObject,
  useEffect,
  useRef,
  useState,
  forwardRef,
} from "react";
import {
  ButtonTypesMulti,
  SliderMultipleValueProps,
  RefsMulti,
} from "./slider-multiple-value.types";
import {
  changeValuesAndUpdate,
  computeOffset,
  computeTrackWidth,
  computeValueBasedOnDragPosition,
  getNumberOfDecimalPlaces,
  updateValuesOnBlur,
} from "./slider.util";
import classNames from "classnames";
import {
  ChangeValuesAndUpdate,
  MouseOrTouchEvent,
  ReactMouseOrTouchEvent,
} from "./slider.types";
import { getOffset, getSliderWidth } from "./slider.util";
import uniqueId from "lodash.uniqueid";
import { TextInputBase } from "../text-input/text-input-base.jsx";
import {
  showSSRHydrationWarning,
  isPropEmpty,
} from "../utils/SSR/showSSRHydrationWarning";
import { useSSR } from "../utils/SSR/useSSR";

export interface SliderMultipleValueWithForwardRef
  extends React.ForwardRefExoticComponent<
    SliderMultipleValueProps & React.RefAttributes<() => RefsMulti>
  > {}

export const SliderMultipleValue = forwardRef<
  () => RefsMulti,
  SliderMultipleValueProps
>(
  (
    {
      label,
      minValue: minValueProp,
      maxValue: maxValueProp,
      minRange,
      maxRange,
      startTitle = "Start",
      endTitle = "End",
      disabled = false,
      step = "1",
      onChange,
      minValueInputName = "slider-multi-1",
      maxValueInputName = "slider-multi-2",
      defaultMaxValue,
      defaultMinValue,
      alternate = false,
      className,
      startInputFieldId: startInputFieldIdProp,
      endInputFieldId: endInputFieldIdProp,
      _timeRangePrivateProps,
      ...rest
    },
    ref
  ): React.ReactElement | null => {
    const {
      renderMinInput,
      renderMaxInput,
      timeSliderInputSeparatorClassName,
      timeSliderClassName,
      minCeilValue,
      maxFloorValue,
      startButtonProps = {},
      endButtonProps = {},
    } = _timeRangePrivateProps || {};
    const { minValueRef, maxValueRef } = (ref as any)?.() || {};
    const defaultMinValueComputed =
      defaultMinValue !== undefined ? defaultMinValue : 0;
    const defaultMaxValueComputed =
      defaultMaxValue !== undefined ? defaultMaxValue : 0;
    const minValueComputed =
      minValueProp !== undefined ? minValueProp : defaultMinValueComputed;
    const maxValueComputed =
      maxValueProp !== undefined ? maxValueProp : defaultMaxValueComputed;
    const [minValue, setMinValue] = useState<number>(minValueComputed);
    const [maxValue, setMaxValue] = useState<number>(maxValueComputed);
    const [minActive, setMinActive] = useState(false);
    const [maxActive, setMaxActive] = useState(false);

    const [numberOfDecimalPlaces, setNumberOfDecimalPlaces] = useState(
      getNumberOfDecimalPlaces(step)
    );
    const [stepValue, setStepValue] = useState(Number(step));

    const [inputMinValue, setInputMinValue] = useState<string>(
      minValueComputed.toFixed(numberOfDecimalPlaces)
    );
    const [inputMaxValue, setInputMaxValue] = useState(
      maxValueComputed.toFixed(numberOfDecimalPlaces)
    );

    useEffect(() => {
      setInputMinValue(
        (Math.round(minValueComputed / stepValue) * stepValue).toFixed(
          numberOfDecimalPlaces
        )
      );
    }, [minValueComputed, numberOfDecimalPlaces, stepValue]);

    useEffect(() => {
      setInputMaxValue(maxValueComputed.toFixed(numberOfDecimalPlaces));
    }, [maxValueComputed, numberOfDecimalPlaces]);

    useEffect(() => {
      setNumberOfDecimalPlaces(getNumberOfDecimalPlaces(step));
      setStepValue(Number(step));
    }, [step]);

    const [startBtnOffset, setStartBtnOffset] = useState("0%");

    const [endBtnOffset, setEndBtnOffset] = useState("0%");

    // Ref for track component to get details from dom like width
    const trackEl = useRef<HTMLSpanElement>(null);
    const sliderEl = useRef() as MutableRefObject<HTMLDivElement>;
    const startButtonEl = useRef() as MutableRefObject<HTMLButtonElement>;
    const endButtonEl = useRef() as MutableRefObject<HTMLButtonElement>;

    const { current: startInputFieldUniqueId } = useRef(
      uniqueId("spark-range-slider-")
    );
    const { current: endInputFieldUniqueId } = useRef(
      uniqueId("spark-range-slider-")
    );

    useSSR(() => {
      isPropEmpty(startInputFieldIdProp) &&
        showSSRHydrationWarning("startInputFieldId", "SliderMultipleValue");
      isPropEmpty(endInputFieldIdProp) &&
        showSSRHydrationWarning("endInputFieldId", "SliderMultipleValue");
    });

    const startInputFieldId = startInputFieldIdProp || startInputFieldUniqueId;
    const endInputFieldId = endInputFieldIdProp || endInputFieldUniqueId;

    useEffect(() => {
      setMinValue(minValueComputed);
    }, [minValueComputed]);

    useEffect(() => {
      setMaxValue(maxValueComputed);
    }, [maxValueComputed]);

    useEffect(() => {
      const sliderWidth = getSliderWidth(sliderEl.current);

      setStartBtnOffset(
        computeOffset({
          minValue: minRange,
          maxValue: maxRange,
          value: minValue,
          isButton: true,
          ceilValue: maxValue,
          floorValue: minRange,
          sliderWidth,
          stepValue,
          buttonWidth: startButtonEl.current.offsetWidth,
        }).buttonOffset
      );
      setEndBtnOffset(
        computeOffset({
          minValue: minRange,
          maxValue: maxRange,
          value: maxValue,
          isButton: true,
          ceilValue: maxRange,
          floorValue: minValue,
          sliderWidth,
          stepValue,
          buttonWidth: endButtonEl.current.offsetWidth,
        }).buttonOffset
      );
    }, [minRange, maxRange, minValue, maxValue, stepValue]);

    // Resize slider on window resize
    const [, setResizeTS] = useState(0);
    const onResize = (e: Event): void => setResizeTS(e.timeStamp);
    useEffect(() => {
      window.addEventListener("resize", onResize);
      return () => {
        window.removeEventListener("resize", onResize);
      };
    });

    if (minValue === maxValue || minValue > maxValue) {
      console.warn(
        "minValue prop can't be equal or larger than maxValue in <SliderMultipleValue/>"
      );
      return null;
    }

    const minBtnClass = classNames("spark-slider__handle", {
      active: minActive,
      disabled: _timeRangePrivateProps && disabled,
    });

    const maxBtnClass = classNames("spark-slider__handle", {
      active: maxActive,
      disabled: _timeRangePrivateProps && disabled,
    });

    const getActiveIndex = (): number | undefined => {
      if (minActive) {
        return 0;
      }
      if (maxActive) {
        return 1;
      }
      return undefined;
    };

    const updateMinValue = (newMinValue: number): void => {
      // Check if newMinValue is greater than maxValue. This would occur when user enters value
      // directly into the input fields. If yes, then newMinValue would be one step less than maxValue

      if (minCeilValue && newMinValue > minCeilValue) {
        return;
      }

      const derivedMinValue =
        newMinValue >= maxValue ? maxValue - stepValue : newMinValue;
      setInputMinValue(
        (Math.round(derivedMinValue / stepValue) * stepValue).toFixed(
          numberOfDecimalPlaces
        )
      );
      setMinValue(Math.round(derivedMinValue / stepValue) * stepValue);
      onChange?.({
        min: Math.round(derivedMinValue / stepValue) * stepValue,
        max: maxValue,
        activeThumb: 0,
      });
    };

    const updateMaxValue = (newMaxValue: number): void => {
      // Check if newMaxValue is lesser than minValue. This would occur when user enters value
      // directly into the input fields. If yes, then newMaxValue would be one step more than minValue

      if (maxFloorValue && newMaxValue < maxFloorValue) {
        return;
      }

      let computedMaxValue =
        newMaxValue <= minValue ? minValue + stepValue : newMaxValue;
      if (computedMaxValue >= maxRange) {
        computedMaxValue = maxRange;
      }
      setInputMaxValue(
        (Math.round(computedMaxValue / stepValue) * stepValue).toFixed(
          numberOfDecimalPlaces
        )
      );
      setMaxValue(Math.round(computedMaxValue / stepValue) * stepValue);
      onChange?.({
        min: minValue,
        max: Math.round(computedMaxValue / stepValue) * stepValue,
        activeThumb: 1,
      });
    };

    const handleKeyEventsOnInput = (
      event: React.KeyboardEvent,
      identifier: ButtonTypesMulti
    ): void => {
      changeSliderValueOnKeyDown(event, identifier, false);
    };

    /**
     * Handle keystrokes on the button on focus
     * 1. up or right will increment until the maxValue for all buttons
     * 2. down or left will decrement until the minValue for all buttons
     */
    const changeSliderValueOnKeyDown = (
      { key }: React.KeyboardEvent,
      identifier: ButtonTypesMulti,
      isButton: boolean = true
    ): void => {
      if (disabled) {
        return;
      }
      let upDaterFn;
      let currentMinValue;
      let currentMaxValue;
      let currentValue;
      if (identifier === ButtonTypesMulti.START_BUTTON) {
        upDaterFn = updateMinValue;
        currentMinValue = minRange;
        currentMaxValue = maxValue;
        currentValue = isButton ? minValue : Number(inputMinValue);
      } else if (identifier === ButtonTypesMulti.END_BUTTON) {
        upDaterFn = updateMaxValue;
        currentMinValue = minValue;
        currentMaxValue = maxRange;
        currentValue = isButton ? maxValue : Number(inputMaxValue);
      } else {
        return;
      }

      changeValuesAndUpdate({
        key,
        upDaterFn,
        currentMinValue,
        currentValue,
        step: stepValue,
        decimalsInStep: numberOfDecimalPlaces,
        currentMaxValue,
        isButton,
      } as ChangeValuesAndUpdate);
    };

    const onMouseMoveStart = (e: MouseOrTouchEvent): void => {
      onMouseMove(e, ButtonTypesMulti.START_BUTTON);
    };
    const onMouseMoveEnd = (e: MouseOrTouchEvent): void => {
      onMouseMove(e, ButtonTypesMulti.END_BUTTON);
    };

    // Mouse is moved on the slider. The track fill and button position to be computed
    const onMouseMove = (
      e: MouseOrTouchEvent,
      btnClicked: ButtonTypesMulti
    ): void => {
      const sliderObject = getOffset(sliderEl.current);
      let updaterMethod;
      if (typeof sliderObject === "boolean") {
        return;
      }
      switch (btnClicked) {
        case ButtonTypesMulti.START_BUTTON:
          updaterMethod = updateMinValue;
          break;
        default:
          updaterMethod = updateMaxValue;
      }
      const dragPosition =
        "clientX" in e
          ? e.clientX - sliderObject?.left
          : e.touches[0].clientX - sliderObject?.left;

      // The event occurred in between min and max value range.
      const newValue = computeValueBasedOnDragPosition({
        dragPosition,
        sliderWidth: sliderObject.width,
        minValue: minRange,
        maxValue: maxRange,
        step: stepValue,
        decimalsInStep: numberOfDecimalPlaces,
      });
      updaterMethod(newValue);
    };

    const onMouseUp = (): void => {
      if (disabled) {
        return;
      }

      setMinActive(false);
      setMaxActive(false);

      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("mousemove", onMouseMoveStart);
      window.removeEventListener("mousemove", onMouseMoveEnd);

      window.removeEventListener("touchend", onMouseUp);
      window.removeEventListener("touchmove", onMouseMoveStart);
      window.removeEventListener("touchmove", onMouseMoveEnd);
    };

    const changeSliderValue1 = (e: ReactMouseOrTouchEvent): void => {
      startButtonEl.current.focus();
      if (disabled) {
        return;
      }
      e.cancelable && e.preventDefault();

      setMinActive(true);

      window.addEventListener("mousemove", onMouseMoveStart);
      window.addEventListener("mouseup", onMouseUp);

      window.addEventListener("touchmove", onMouseMoveStart);
      window.addEventListener("touchend", onMouseUp);
    };

    const changeSliderValue2 = (e: ReactMouseOrTouchEvent): void => {
      endButtonEl.current.focus();
      if (disabled) {
        return;
      }
      e.cancelable && e.preventDefault();

      setMaxActive(true);

      window.addEventListener("mousemove", onMouseMoveEnd);
      window.addEventListener("mouseup", onMouseUp);

      window.addEventListener("touchmove", onMouseMoveEnd);
      window.addEventListener("touchend", onMouseUp);
    };

    const sliderClassNames = classNames(
      {
        "spark-slider--secondary": alternate,
      },
      "spark-range-slider",
      className
    );
    return (
      <div
        className={timeSliderClassName || sliderClassNames}
        data-active-index={getActiveIndex()}
        {...rest}
      >
        <label className="spark-label" htmlFor={startInputFieldId}>
          {label}
        </label>
        {renderMinInput ? (
          renderMinInput(startInputFieldId)
        ) : (
          <TextInputBase
            name={minValueInputName?.toLowerCase() || ""}
            type="number"
            id={startInputFieldId}
            min={minRange}
            max={maxValue - stepValue}
            value={inputMinValue}
            step={step}
            title={startTitle}
            aria-label={startTitle}
            disabled={disabled}
            ref={minValueRef}
            onChange={(e): void => setInputMinValue(e.target.value)}
            onBlur={(): void =>
              updateValuesOnBlur({
                updaterFn: updateMinValue,
                minValue: minRange,
                maxValue,
                valueFromInput: inputMinValue,
              })
            }
            onKeyDown={(e): void =>
              handleKeyEventsOnInput(e, ButtonTypesMulti.START_BUTTON)
            }
            isSlider={true}
          />
        )}
        <span
          className={
            timeSliderInputSeparatorClassName ||
            "spark-range-slider__input-divider"
          }
        />
        {renderMaxInput ? (
          renderMaxInput(endInputFieldId)
        ) : (
          <TextInputBase
            id={endInputFieldId}
            type="number"
            name={maxValueInputName?.toLowerCase() || ""}
            min={minValue + stepValue}
            max={maxRange}
            value={inputMaxValue}
            step={step}
            title={endTitle}
            aria-label={endTitle}
            disabled={disabled}
            ref={maxValueRef}
            onChange={(e): void => setInputMaxValue(e.target.value)}
            onKeyDown={(e): void =>
              handleKeyEventsOnInput(e, ButtonTypesMulti.END_BUTTON)
            }
            onBlur={(): void =>
              updateValuesOnBlur({
                updaterFn: updateMaxValue,
                minValue,
                maxValue: maxRange,
                valueFromInput: inputMaxValue,
              })
            }
            isSlider={true}
          />
        )}
        <div ref={sliderEl} className="spark-slider__controls">
          <button
            type="button"
            tabIndex={disabled ? -1 : undefined}
            ref={startButtonEl}
            className={minBtnClass}
            title={startTitle}
            role="slider"
            disabled={disabled}
            aria-orientation="horizontal"
            aria-valuemin={minRange}
            aria-valuemax={maxValue - stepValue}
            aria-valuenow={minValue}
            aria-valuetext={"" + minValue}
            aria-label={startTitle}
            aria-controls={startInputFieldId}
            data-value={minValue}
            onTouchStart={changeSliderValue1}
            onMouseDown={changeSliderValue1}
            onFocus={(): void => setMinActive(true)}
            onBlur={(): void => setMinActive(false)}
            style={{ left: startBtnOffset }}
            onKeyDown={(e): void =>
              changeSliderValueOnKeyDown(e, ButtonTypesMulti.START_BUTTON)
            }
            {...startButtonProps}
          />
          <button
            type="button"
            tabIndex={disabled ? -1 : undefined}
            ref={endButtonEl}
            className={maxBtnClass}
            title={endTitle}
            role="slider"
            disabled={disabled}
            aria-orientation="horizontal"
            aria-valuemin={minValue + stepValue}
            aria-valuemax={maxRange}
            aria-valuenow={maxValue}
            aria-valuetext={"" + maxValue}
            aria-label={endTitle}
            aria-controls={endInputFieldId}
            data-value={maxValue}
            onTouchStart={changeSliderValue2}
            onMouseDown={changeSliderValue2}
            onFocus={(): void => setMaxActive(true)}
            onBlur={(): void => setMaxActive(false)}
            style={{ left: endBtnOffset }}
            onKeyDown={(e): void =>
              changeSliderValueOnKeyDown(e, ButtonTypesMulti.END_BUTTON)
            }
            {...endButtonProps}
          />
          <span className="spark-slider__track" ref={trackEl}>
            <span
              className="spark-slider__track-fill"
              style={{
                width: computeTrackWidth(startBtnOffset, endBtnOffset),
                left: startBtnOffset,
              }}
            />
          </span>
        </div>
      </div>
    );
  }
) as SliderMultipleValueWithForwardRef;

SliderMultipleValue.displayName = "SliderMultipleValue";
